import { Box, Container, keyframes, styled, type SxProps } from '@mui/material';
import React from 'react';

import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { mergeSx } from '@/lib/utils.ts';

import { Boat } from './Boat.tsx';
import wave from './wave.svg';

// Svg file dimensions
const waterSize = {
  width: 131,
  height: 14,
};

const waterScale = 1.5;

const slideAnim = keyframes({
  '0%': {
    transform: 'translateX(0)',
  },
  '100%': {
    transform: `translateX(-${waterSize.width * waterScale}px)`,
  },
});

const Wave = styled('div')({
  '--speed': '8s',
  animation: `${slideAnim} var(--speed) linear infinite`,
  width: `calc(100vw + ${waterSize.width * waterScale}px * 2)`,
  height: waterSize.height * waterScale,
  backgroundImage: `url("${wave}")`,
  backgroundSize: `${waterSize.width * waterScale}px ${waterSize.height * waterScale}px`,
});

const angle = 5;
const initialAngle = 1;

const wiggleAnim = keyframes({
  '0%': {
    rotate: `${angle - initialAngle}deg`,
  },
  '100%': {
    rotate: `-${angle + initialAngle}deg`,
  },
});

const bounceAnim = keyframes({
  '0%': {
    transform: `translateY(3px)`,
  },
  '100%': {
    transform: `translateY(0)`,
  },
});

const StyledBoat = styled(Boat)({
  position: 'absolute',
  top: -40,
  left: `calc(50% - ${waterSize.width / 2}px)`,
  animation: `
    ${wiggleAnim} 1s ease-in-out alternate infinite,
    ${bounceAnim} 0.7s ease-in-out alternate infinite
  `,
});

export type BoatFooterProps = {
  disclaimer?: boolean;
  waterSx?: SxProps;
};

export const BoatFooter: React.FC<BoatFooterProps> = ({ disclaimer, waterSx }) => {
  return (
    <Box
      sx={{
        mt: 'auto',
        pt: 4,
        overflow: 'hidden',
        width: '100%',
        isolation: 'isolate',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          mt: 4,
        }}
      >
        <StyledBoat />
        <Wave />
        <Wave
          sx={{
            '--speed': '12s',
            position: 'absolute',
            opacity: 0.5,
            zIndex: -1,
            top: -2,
          }}
        />
      </Box>
      <Box
        sx={mergeSx(
          {
            pt: { xs: 1.5, sm: 3 },
            pb: { xs: 3, sm: 4 },
            mt: '-2px',
          },
          waterSx,
          { backgroundColor: '#74C6FD' },
        )}
      >
        {disclaimer && (
          <Container maxWidth="md">
            <div className="text-center typography-v1-body4 text-balance text-v1-background-200">
              <LoanExampleText />
            </div>
          </Container>
        )}
      </Box>
    </Box>
  );
};
